/**
 * Sammelstelle für einen gemeinsamen Speichern-Knopf (Regel „Dokument-Navigation" R5):
 * Karten und Abschnitte einer Seite melden sich an; der eine Knopf der Seite speichert
 * alle geänderten Teile. Verallgemeinert die Flush-Funktion der Arbeitszeitmodell-Karte
 * in der Mitarbeiterakte. Ohne React, damit die Reihenfolge und das Verhalten bei
 * Teilfehlern testbar sind.
 */
export interface SaveParticipant {
  id: string;
  /** Für Meldungen, z. B. „Gruppen" */
  label: string;
  isDirty: () => boolean;
  /** true = gespeichert; false oder Fehler = nicht gespeichert */
  flush: () => Promise<boolean>;
  discard?: () => void;
}

export interface FlushResult {
  ok: boolean;
  saved: string[];
  /** Bezeichnungen der Teile, die nicht gespeichert wurden */
  failed: string[];
}

export interface SaveRegistry {
  register(p: SaveParticipant): () => void;
  /** Ein Teil meldet, dass sich sein Zustand geändert hat */
  touch(): void;
  anyDirty(): boolean;
  dirtyLabels(): string[];
  /** Speichert alle geänderten Teile der Reihe nach; ein Fehler hält die übrigen nicht auf */
  flushAll(): Promise<FlushResult>;
  discardAll(): void;
  subscribe(cb: () => void): () => void;
}

export function createSaveRegistry(): SaveRegistry {
  const parts = new Map<string, SaveParticipant>();
  const listeners = new Set<() => void>();
  const notify = (): void => { for (const l of listeners) l(); };
  const dirtyParts = (): SaveParticipant[] => [...parts.values()].filter((p) => {
    try { return p.isDirty(); } catch { return false; }
  });

  return {
    register(p) {
      parts.set(p.id, p);
      notify();
      return () => {
        if (parts.get(p.id) === p) { parts.delete(p.id); notify(); }
      };
    },
    touch: notify,
    anyDirty: () => dirtyParts().length > 0,
    dirtyLabels: () => dirtyParts().map((p) => p.label),
    async flushAll() {
      const saved: string[] = [];
      const failed: string[] = [];
      for (const p of dirtyParts()) {
        let ok = false;
        try { ok = await p.flush(); } catch { ok = false; }
        (ok ? saved : failed).push(p.label);
      }
      notify();
      return { ok: failed.length === 0, saved, failed };
    },
    discardAll() {
      for (const p of dirtyParts()) {
        try { p.discard?.(); } catch { /* ein Teil, der nicht verwerfen kann, hält die übrigen nicht auf */ }
      }
      notify();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => { listeners.delete(cb); };
    },
  };
}
